import { ref, computed, onMounted, onUnmounted } from 'vue'
import ApiService from '@/services/api.js'
import { importSummaryFromTask, LOST_IMPORT } from '@/utils/importSummary.js'

const STORAGE_KEY = 'linkflare_import_task'
// The finished import's summary, kept until the user dismisses it so a
// closed tab still sees what landed and what did not.
const RESULT_KEY = 'linkflare_import_result'
const POLL_INTERVAL = 3000 // 3 seconds

export function useImportTask(getAuthToken) {
  const currentTask = ref(null)
  const taskProgress = ref(0)
  const urlsImported = ref(0)
  const lastUrlsImported = ref(0) // Track last known count
  const importResult = ref(null)
  let pollInterval = null

  const hasActiveTask = computed(() => currentTask.value !== null)
  const progressPercent = computed(() => Math.round(taskProgress.value * 100))
  
  const estimatedTimeRemaining = computed(() => {
    if (!currentTask.value) return 0
    const remaining = currentTask.value.urls_found - urlsImported.value
    return Math.ceil(remaining * 5) // 5 seconds per URL
  })

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds} seconds`
    const totalMinutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60)
      const mins = totalMinutes % 60
      if (mins === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`
      return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`
    }
    
    if (secs === 0) return `${totalMinutes} minute${totalMinutes !== 1 ? 's' : ''}`
    return `${totalMinutes} minute${totalMinutes !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`
  }

  const saveTaskToStorage = (task) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(task))
    } catch (e) {
      console.error('Failed to save import task to localStorage:', e)
    }
  }

  const loadTaskFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (e) {
      console.error('Failed to load import task from localStorage:', e)
    }
    return null
  }

  const clearTaskFromStorage = () => {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      console.error('Failed to clear import task from localStorage:', e)
    }
  }

  const setResult = (result) => {
    importResult.value = result
    try {
      localStorage.setItem(RESULT_KEY, JSON.stringify(result))
    } catch (e) {
      console.error('Failed to save import result to localStorage:', e)
    }
  }

  const dismissResult = () => {
    importResult.value = null
    try {
      localStorage.removeItem(RESULT_KEY)
    } catch (e) {
      console.error('Failed to clear import result from localStorage:', e)
    }
  }

  const startTask = (taskData) => {
    dismissResult()
    currentTask.value = taskData
    taskProgress.value = 0
    urlsImported.value = 0
    lastUrlsImported.value = 0
    saveTaskToStorage(taskData)
    startPolling()
  }

  const updateProgress = async () => {
    if (!currentTask.value) return
    const taskId = currentTask.value.task_id
    // finished, replaced or forgotten (sign-out) while a request was in flight
    const stale = () => !currentTask.value || currentTask.value.task_id !== taskId

    try {
      const token = await getAuthToken()
      if (stale()) return
      if (!token) {
        console.error('No auth token available for progress update')
        return
      }

      const progress = await ApiService.getTaskProgress(taskId, token)
      if (stale()) return

      if (progress === null) {
        // 404. A backend that reports `status` keeps finished tasks, so a
        // tracked task that vanished was lost (API restart, expired result).
        // An older backend drops every task the moment it ends: stay silent.
        completeTask(currentTask.value.tracked ? LOST_IMPORT : null)
      } else {
        const previousUrlsImported = urlsImported.value
        const tracked = currentTask.value.tracked || typeof progress.status === 'string'
        taskProgress.value = progress.progress
        urlsImported.value = progress.urls_imported

        // Update stored task with latest progress
        if (tracked && !currentTask.value.tracked) currentTask.value = { ...currentTask.value, tracked }
        saveTaskToStorage({
          ...currentTask.value,
          progress: progress.progress,
          urls_imported: progress.urls_imported
        })

        // Emit event if new URLs were imported since last check
        if (progress.urls_imported > previousUrlsImported) {
          window.dispatchEvent(new CustomEvent('import-progress-update', {
            detail: {
              task_id: currentTask.value.task_id,
              urls_imported: progress.urls_imported,
              new_urls: progress.urls_imported - previousUrlsImported,
              progress: progress.progress
            }
          }))
        }

        // Check if complete
        if (tracked) {
          if (progress.status === 'done') completeTask(importSummaryFromTask(progress))
        } else if (progress.progress >= 1.0) {
          completeTask()
        }
      }
    } catch (error) {
      console.error('Failed to update import task progress:', error)
    }
  }

  const startPolling = () => {
    if (pollInterval) return // Already polling
    
    pollInterval = setInterval(() => {
      updateProgress()
    }, POLL_INTERVAL)
    
    // Initial update
    updateProgress()
  }

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }
  }

  // `result` is the import's summary (or LOST_IMPORT); null when there is
  // nothing to report, e.g. the user dismissed the progress bar.
  const completeTask = (result = null) => {
    stopPolling()
    
    // Emit completion event
    if (currentTask.value) {
      window.dispatchEvent(new CustomEvent('import-task-complete', {
        detail: {
          task_id: currentTask.value.task_id,
          urls_imported: urlsImported.value,
          summary: result
        }
      }))
    }
    
    currentTask.value = null
    taskProgress.value = 0
    urlsImported.value = 0
    lastUrlsImported.value = 0
    clearTaskFromStorage()
    if (result) setResult(result)
  }

  const cancelTask = () => {
    completeTask()
  }

  // Sign-out: the next account on this browser must not see this import. No
  // completion event, since nothing finished and the views are being torn down.
  const forget = () => {
    stopPolling()
    currentTask.value = null
    taskProgress.value = 0
    urlsImported.value = 0
    lastUrlsImported.value = 0
    clearTaskFromStorage()
    dismissResult()
  }

  // Load existing task on mount
  onMounted(() => {
    try {
      const storedResult = localStorage.getItem(RESULT_KEY)
      if (storedResult) importResult.value = JSON.parse(storedResult)
    } catch (e) {
      console.error('Failed to load import result from localStorage:', e)
    }
    const stored = loadTaskFromStorage()
    if (stored) {
      currentTask.value = stored
      taskProgress.value = stored.progress || 0
      urlsImported.value = stored.urls_imported || 0
      startPolling()
    }
  })

  // Cleanup on unmount
  onUnmounted(() => {
    stopPolling()
  })

  return {
    currentTask,
    hasActiveTask,
    progressPercent,
    taskProgress,
    urlsImported,
    estimatedTimeRemaining,
    formatTime,
    startTask,
    updateProgress,
    completeTask,
    cancelTask,
    importResult,
    dismissResult,
    forget
  }
}
